export function createObserver(callback, options = {}) {
    const defaultOptions = { threshold: [0, 0.2, 0.4, 0.6, 0.8, 1] };
    const observerOptions = {
        ...defaultOptions,
        ...options,
    };

    return new IntersectionObserver(callback, observerOptions);
}

export function observeInViewport(elements, options = {}) {
    // elements should be a nodelist of html elements such as returned by `querySelectorAll`
    if (!elements) {
        return;
    }

    const observer = createObserver(viewportObserver, options);

    // type checking to prevent errors
    if (elements instanceof NodeList) {
        elements.forEach((element) => {
            if (element instanceof HTMLElement) {
                observer.observe(element);
            }
        });
    } else if (elements instanceof HTMLElement) {
        observer.observe(elements);
    }
}

function viewportObserver(entries) {
    const classes = {
        intersecting: 'is-in-viewport',
        wasIntersecting: 'was-in-viewport',
        outTop: 'is-out-of-viewport-top',
        outBottom: 'is-out-of-viewport-bottom',
        enteringFromTop: 'entering-from-top',
        enteringFromBottom: 'entering-from-bottom',
        leavingFromTop: 'leaving-at-top',
        leavingFromBottom: 'leaving-at-bottom',
    };

    // here we check if the entry is currently intersecting
    entries.forEach((entry) => {
        const { isIntersecting, target, intersectionRatio } = entry;

        if (isIntersecting) {
            // remove the out of viewport classes
            target.classList.remove(classes.outTop, classes.outBottom);

            // element is partially visible
            if (intersectionRatio < 1) {
                const bounding = target.getBoundingClientRect();
                const isLeaving = target.classList.contains(classes.intersecting);

                // if bounding.top is negative the element is at the top of the screen else it must be at the bottom
                if (isLeaving) {
                    if (
                        !target.classList.contains(classes.leavingFromTop) &&
                        !target.classList.contains(classes.leavingFromBottom)
                    ) {
                        // double check that we are not in the process of entering
                        if (bounding.top < 0) {
                            // element is at top
                            if (!target.classList.contains(classes.enteringFromTop)) {
                                target.classList.add(classes.leavingFromTop);
                            }
                        } else {
                            if (!target.classList.contains(classes.enteringFromBottom)) {
                                target.classList.add(classes.leavingFromBottom);
                            }
                        }
                    }
                } else {
                    if (
                        !target.classList.contains(classes.enteringFromTop) &&
                        !target.classList.contains(classes.enteringFromBottom)
                    ) {
                        target.classList.add(bounding.top < 0 ? classes.enteringFromTop : classes.enteringFromBottom);
                    }
                }
            } else {
                // clean up possible entering/leaving classes
                target.classList.remove(
                    classes.enteringFromTop,
                    classes.enteringFromBottom,
                    classes.leavingFromTop,
                    classes.leavingFromBottom
                );
            }

            // add is in viewport if not there yet
            if (!target.classList.contains(classes.intersecting)) {
                target.classList.add(classes.intersecting);
            }
        } else {
            const bounding = target.getBoundingClientRect();

            // bounding.top is negative if element is hidden behind top of viewport > else must be at bottom
            target.classList.add(bounding.top < 0 ? classes.outTop : classes.outBottom);

            // clean up possible entering/leaving classes
            target.classList.remove(
                classes.enteringFromTop,
                classes.enteringFromBottom,
                classes.leavingFromTop,
                classes.leavingFromBottom
            );

            if (
                target.classList.contains(classes.intersecting) &&
                !target.classList.contains(classes.wasIntersecting)
            ) {
                target.classList.add(classes.wasIntersecting);
            }

            // remove the in viewport class
            target.classList.remove(classes.intersecting);
        }
    });
}
